import api from "./api";

export interface SalaryCalculationResponse {
  results: SalaryRecord[];
}

export interface SalaryRecord {
  salary_id: string | number;
  salarytID?: string | number;
  employee_id: string;
  emptID?: string;
  employeeID: string;
  name: string;
  profile_photo_url?: string | null;
  calculationType: string;
  mode: string;
  status: string;
  department: string;
  designation: string;
  joiningDate: string;
  SalaryDate: string;
  month: number;
  year: number;
  monthlySalary: number;
  // Declared monthly take-home (net after TDS/PF/ESIC/PT/LWF), shown in the
  // Monthly column. Null when the employee has no declaration for the period.
  takeHome?: number | null;
  netSalary: number;
  bonus: number;
  extrapay: number;
  deductions: number;
  isApproved: boolean;
  // Approval-lifecycle flags returned by the calc endpoints (Status column).
  approved?: boolean;
  sent_for_approval?: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  logs?: {
    attendance?: DailyBreakdown[];
  };

}

export interface DailyBreakdown {
  date: string;
  hours: number;
  status: string;
  payable: number;
  overtime: number;
  day_salary: number;
  overtime_amount: number;
}

export interface SalaryCalculationLogsResponse {
  success?: boolean;
  salary_id?: string | number;
  employee_id?: string;
  employeeID?: string;
  name?: string;
  month?: number;
  year?: number;
  attendance: DailyBreakdown[];
  summary?: Record<string, number>;
}

export interface ApprovalRequest {
  id: string[];
  year: number;
  month: number;
}

export interface ApprovalResponse {
  success: boolean;
  message: string;
  results?: SalaryRecord[];
  data?: SalaryRecord;
}

export interface SalaryStatusResponse {
  success: boolean;
  status: {
    pending: number;
    approved: number;
    rejected: number;
    total: number;
  };
  results?: SalaryRecord[];
}

export interface ManualSalaryAdjustment {
  employee_id: string;
  bonus: number;
  extrapay: number;
  month: number;
  year: number;
  description?: string;
}

export interface ExportEmployeesResponse {
  success: boolean;
  message: string;
  data?: any;
  download_url?: string;
}

export interface ExportSalaryResponse {
  success: boolean;
  message: string;
  data?: any;
  download_url?: string;
}


export const salaryApi = {
  // Salary settings for the current branch — includes the holiday calendar.
  getSettings: () => api.get('/salary/get_salary_settings'),

  // Employee self-service: the logged-in user's own salary for a period.
  // Only returns the breakdown once the salary has been sent for approval.
  getMySalary: (year: string, month: string) =>
    api.get(`/salary/my_salary?year=${year}&month=${month}`),

  // Original attendance-based calculation
  calculateBulk: (year: string, month: string, employeeIds: string[]) => {
    return api.post(`/salary/calculate/bulk?year=${year}&month=${month}`, {
      id: employeeIds
    });
  },

  // New system-level tracking calculation
  calculateWithSystemTracking: (year: string, month: string, employeeIds: string[]) => {
    return api.post(`/salary/calculate/bulk/system-tracking?year=${year}&month=${month}`, {
      id: employeeIds
    });
  },

  getCalculationLogs: (salaryId: string | number) =>
    api.get<SalaryCalculationLogsResponse>(
      `/salary/calculation/${encodeURIComponent(String(salaryId))}/logs`
    ),

  manualSalaryAdjustment: (data: ManualSalaryAdjustment) => {
    return api.put('/salary/manual_salary_adjustment', data);
  },

  // No Deduction - Attendance Based
calculateNoDeduction: (year: string, month: string, employeeIds: string[]) => {
  return api.post(
    `/salary/re-calculate_no_deduction?year=${year}&month=${month}`,
    { id: employeeIds }
  );
},

// No Deduction - System Tracking
calculateNoDeductionSystem: (year: string, month: string, employeeIds: string[]) => {
  return api.post(
    `/salary/re-calculate_no_deduction/system-tracking?year=${year}&month=${month}`,
    { id: employeeIds }
  );
},


  // Get Approved Salary - POST API
  getApprovedSalaries: (year: string, month: string, employeeIds: string[] = []) => {
    return api.post<ApprovalResponse>(
      `/salary/get_approved_salary?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Approve Salary - PUT API
  approveSalaries: (year: string, month: string, employeeIds: string[]) => {
    return api.put<ApprovalResponse>(
      `/salary/approved_salary?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Get Salary Status - POST API
  getSalaryStatus: (year: string, month: string, employeeIds: string[] = []) => {
    return api.post<SalaryStatusResponse>(
      `/salary/get_salary_status?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Reject Salaries — optional free-text reason is persisted on the record.
  rejectSalaries: (year: string, month: string, employeeIds: string[], reason?: string) => {
    return api.put<ApprovalResponse>(
      `/salary/reject_salary?year=${year}&month=${month}`,
      { id: employeeIds, reason }
    );
  },

  // Send generated salaries for approval (notifies employees; status stays PENDING)
  sendForApproval: (year: string, month: string, employeeIds: string[]) =>
    api.post(`/salary/send_for_approval?year=${year}&month=${month}`, { id: employeeIds }),

 exportEmployees: () => {
  return api.get(`/export_employees`);
},

  // NEW: Export Salary - POST API
  exportSalary: (year: string, month: string, employeeIds: string[] = []) => {
    return api.post<ExportSalaryResponse>(
      `/export_salary?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Download the bank "Salary Transfer Sheet" (.xlsx) in the standard template
  // format (styled headers + employee/bank details + net payable). Returns the
  // file as a Blob so the caller can trigger a browser download.
  exportSalaryTransferSheet: (year: string, month: string, employeeIds: string[] = []) => {
    return api
      .post(
        `/salary/export_salary_transfer_sheet?year=${year}&month=${month}`,
        { id: employeeIds },
        { responseType: "blob" }
      )
      .then((res) => res.data as Blob);
  },

  // ---- Retainer-ship ----
  getRetainer: (employeeId: string) =>
    api.get(`/salary/retainer/${employeeId}`),

  updateRetainer: (employeeId: string, data: any) =>
    api.put(`/salary/retainer/${employeeId}`, data),

  releaseRetainer: (employeeId: string, hold = false) =>
    api.post(`/salary/retainer/${employeeId}/release`, { hold }),

  getRetainerReport: () =>
    api.get(`/salary/retainer/report`),

};

// ---- Salary notifications (bell) ----
export const getSalaryNotifications = (limit = 50) =>
  api.get(`/salary/salary_notifications/unread?limit=${limit}`);

export const markSalaryNotificationRead = (id: string) =>
  api.post(`/salary/salary_notifications/${id}/read`);
